"use client";

import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import type { DateRange } from "react-day-picker";
import dayjs from "dayjs";
import DatePicker from "@/components/DatePicker";
import { Typography } from "@/components/Typography";

export interface FormDateRangeFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  placeholder?: string;
  leadingIcon?: React.ReactNode;
  disabled?: boolean;
  rounded?: "normal" | "full";
  align?: "start" | "center" | "end";
}

const parseDateRange = (value: unknown): DateRange | undefined => {
  if (!value) return undefined;
  if (typeof value === "object" && value !== null && "from" in value) {
    const r = value as DateRange;
    return {
      from: r.from ? dayjs(r.from).toDate() : undefined,
      to: r.to ? dayjs(r.to).toDate() : undefined,
    };
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parts = trimmed.split(" - ");
    if (parts.length === 2) {
      const from = dayjs(parts[0]).toDate();
      const to = dayjs(parts[1]).toDate();
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
        return { from, to };
      }
    } else if (parts.length === 1 && parts[0].trim()) {
      const from = dayjs(parts[0]).toDate();
      if (!isNaN(from.getTime())) {
        return { from, to: undefined };
      }
    }
  }
  return undefined;
};

const formatDateRange = (range?: DateRange): string => {
  if (!range?.from) return "";
  if (!range.to || dayjs(range.from).isSame(range.to, "day")) {
    return dayjs(range.from).format("DD MMM YYYY");
  }
  return `${dayjs(range.from).format("DD MMM YYYY")} - ${dayjs(range.to).format("DD MMM YYYY")}`;
};

const FormDateRangeField = <TFieldValues extends FieldValues>({
  name,
  control,
  label = "Date",
  placeholder = "Select check-in & check-out date",
  leadingIcon,
  rounded = "normal",
  align = "start",
}: FormDateRangeFieldProps<TFieldValues>) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => {
      const selectedRange = parseDateRange(field.value);

      const handleChange = (range: DateRange | undefined) => {
        field.onChange(formatDateRange(range));
      };

      return (
        <div className="flex flex-col gap-1.5">
          {label && <Typography variant="button-sm">{label}</Typography>}
          <DatePicker
            mode="range"
            ariaLabel={label}
            emptyLabel={placeholder}
            value={selectedRange}
            onChange={handleChange}
            rounded={rounded}
            align={align}
            leadingIcon={leadingIcon}
            error={!!fieldState.error}
          />
          {fieldState.error && (
            <Typography variant="body2" className="text-error-foreground">
              {fieldState.error.message}
            </Typography>
          )}
        </div>
      );
    }}
  />
);

export default FormDateRangeField;
